import { StateController } from 'state-controller';
import { v4 as uuidV4 } from 'uuid';
import { AppState } from 'redux/store';
import { t } from 'setup-localization';
import { protocol } from 'utils/env-helpers';
import { AccessLevel, Permission } from 'services/permission.model';
import { PermissionGuardActions } from 'modules/permission-guard/permission-guard.controller';
import { DeleteConfirmationOwnProps } from 'modules/root-modals/modals/confirmation-modal/confirmation-modal';
import { ModalActions } from 'modules/root-modals/root-modals.controller';
import { MODALS } from 'modules/root-modals/modals';
import FireMinusIcon from 'icons/fire-minus';
import { Trans } from 'react-i18next';
import { notify } from 'notifications';
import { ProductionWorkflowAdditionalTaskT, ProductionWorkflowTaskT } from 'services/production-workflow.model';
import { ProductionTaskService } from 'services/production-task.service';
import { ProductionStatusEnum, TaskStatusEnum } from 'types/status-enums';
import { PriorityEnum } from 'types/priority-enums';
import { TaskAssigmentService } from 'services/task-assigment.service';
import { UserShortModel } from 'services/user.model';
import { Actions as ProductionWorkflowActions } from 'pages/production-workflow/controllers/production-workflow.controller';
import { Actions as ManageFailedTasksActions } from 'modules/manage-failed-tasks-modal/manage-failed-tasks.controller';
import { ProductionFiltersActions } from 'pages/production/controllers/production-filters-controller/production-filters.controller';
import { IdName } from 'types/common-types';
import { PaginationMode } from 'pages/production/controllers/production-filters-controller/constants';
import ReopenEmojiIcon from 'icons/reopen-emoji-icon';
import { FilledPlusIcon } from 'icons/filled-plus';
import { taskDoneStatuses } from 'pages/production-workflow/controllers/helpers';
import ConfirmationWindow from 'modules/confirmation-window/confirmation-window';
import { Actions as ConfirmationWindowActions } from 'modules/confirmation-window/confirmation-window.controller';
import { TaskReopenModel, UpdateTaskBody } from 'services/production-task.model';

export type ProductionWorkflowTaskTWithIssues = ProductionWorkflowAdditionalTaskT & {
  issues?: IdName[];
  isFail?: boolean;
  isReopened?: boolean;
  isWithMoney?: boolean;
  disable?: boolean;
  isOpenCollapseWIthHierarchy?: boolean;
  reopenedTask?: ProductionWorkflowTaskTWithIssues;
};

export type ProductionWorkflowAdditionalTasksState = {
  isOpen: boolean;
  isLoadingUsers: boolean;
  tasks: ProductionWorkflowTaskTWithIssues[];
};

const defaultState: ProductionWorkflowAdditionalTasksState = {
  tasks: [],
  isOpen: false,
  isLoadingUsers: false,
};

const stateController = new StateController<ProductionWorkflowAdditionalTasksState>(
  'PRODUCTION_WORKFLOW_ADDITIONAL_TASKS',
  defaultState,
);

export class Actions {
  public static onChange(values: Partial<ProductionWorkflowAdditionalTasksState>) {
    return (dispatch) => dispatch(stateController.setState((prev) => ({ ...prev, ...values })));
  }

  public static toggleAdditionalTasksModal() {
    return (dispatch) => dispatch(stateController.setState((prev) => ({ ...prev, isOpen: !prev.isOpen })));
  }

  public static addTemporaryAdditionalTask() {
    return async (dispatch, getState: () => AppState) => {
      const accessLevels = [AccessLevel.userDepartment, AccessLevel.userDepartmentSubDepartments, AccessLevel.allDepartments];
      if (!dispatch(PermissionGuardActions.checkPermissionAndShowModal(Permission.webTaskEditLevel, accessLevels, []))) {
        return;
      }
      const { page, productionId } = getState().manage_failed_tasks;
      const { tasks } = getState().production_workflow_additional_tasks;
      const { id } = getState().production_workflow.productionInfo;

      const temporaryId = `new-${uuidV4()}`;

      const temporaryAdditionalTask = {
        name: '',
        id: temporaryId,
        basic_reward: 0,
        is_control: false,
        is_in_queue: false,
        is_reopened: false,
        is_additional: true,
        time_limit: 0,
        due_date: null,
        order: tasks.length,
        production_workflow_id: page === 'production' ? id : productionId,

        status: TaskStatusEnum.To_Do,
        priority: PriorityEnum.Medium,
        total_in_progress_overtime: 0,
        description: 'Additional Task',
        responsibilities: [
          {
            taskSlots: [
              {
                id: uuidV4(),
                task_responsibility_id: '',
                taskAssignment: { user: { id: '' } },
              },
            ],
          },
        ],
        issues: [],
      };

      dispatch(
        stateController.setState((prev) => ({
          ...prev,
          tasks: [...tasks, temporaryAdditionalTask],
        })),
      );
    };
  }

  public static updateTemporaryAdditionalTask(temporaryId: string, name: string) {
    return async (dispatch, getState: () => AppState) => {
      const { tasks } = getState().production_workflow_additional_tasks;

      const temporaryAdditionalTask = tasks.find((task) => task.id === temporaryId);

      if (!temporaryAdditionalTask) return;

      const updatedTask = { ...temporaryAdditionalTask, name };

      dispatch(
        stateController.setState((prev) => ({
          ...prev,
          tasks: tasks.map((task) => (task.id === temporaryId ? updatedTask : task)),
        })),
      );

      if (updatedTask.id) {
        dispatch(ManageFailedTasksActions.updateAdditionalTasks(updatedTask, temporaryId));
      }
    };
  }

  public static createAdditionalTask(temporaryId: string, name: string) {
    return async (dispatch, getState: () => AppState) => {
      const { tasks } = getState().production_workflow_additional_tasks;

      const { status } = getState().production_workflow.productionInfo;

      const temporaryAdditionalTask = tasks.find((task) => task.id === temporaryId);

      if (!temporaryAdditionalTask) return;

      const isProductionHasDoneStatus = status === ProductionStatusEnum.Done;

      const modalTitle = isProductionHasDoneStatus ? (
        'Are you sure you want to reopen the production?'
      ) : (
        <>
          Are you sure you want to add the <br /> task?
        </>
      );

      try {
        const reasonOptions = await ProductionTaskService.getAllFailReasons();

        dispatch(ConfirmationWindowActions.setReasonOptions(reasonOptions));

        dispatch(
          ModalActions.openModal<DeleteConfirmationOwnProps>({
            id: MODALS.CONFIRM,
            props: {
              title: modalTitle,
              text: (
                <ConfirmationWindow
                  description="Describe the reason why you want to add this task. It will be saved and appear in task header."
                  warningText={
                    <p>
                      Adding Additional task to the finished production will cause the production status change to In progress
                    </p>
                  }
                  isProductionHasDoneStatus={isProductionHasDoneStatus}
                  onReasonChange={(value) => dispatch(ConfirmationWindowActions.onReasonChange(value))}
                  onReasonParametersChange={(value) => dispatch(ConfirmationWindowActions.onReasonParametersChange(value.id))}
                />
              ),
              icon: isProductionHasDoneStatus ? <ReopenEmojiIcon /> : <FilledPlusIcon />,
              backgroundColor: '#DBE9FF',
              withCloseButton: false,
              actionText: 'Save',
              actionButtonColor: 'primary',
              action: () => dispatch(Actions.handleApplyAddAdditionalTask(temporaryAdditionalTask, temporaryId, name)),
              cancelAction: () => dispatch(ConfirmationWindowActions.disposeState()),
              onClickAway: () => dispatch(ConfirmationWindowActions.disposeState()),
            },
          }),
        );
      } catch (error) {
        notify.error(error.message);
        dispatch(
          stateController.setState((prev) => ({
            ...prev,
            reasonOptions: [],
          })),
        );
      }
    };
  }

  public static handleApplyAddAdditionalTask(
    temporaryAdditionalTask: ProductionWorkflowTaskTWithIssues,
    temporaryId: string,
    name: string,
  ) {
    return async (dispatch, getState: () => AppState) => {
      const { tasks } = getState().production_workflow_additional_tasks;

      const { reasonForReopening, selectedReasonOptions } = getState().confirmation_window;

      const productionWorkflowId = getState().production_workflow.productionInfo.id;

      try {
        const reasonIds = selectedReasonOptions.map((reason) => reason.id);

        const additionalTaskToAddBody = {
          ...temporaryAdditionalTask,
          name,
          fail_reason_ids: reasonIds,
          comment: reasonForReopening,
        };

        const additionalTask = await ProductionTaskService.createTask(additionalTaskToAddBody);

        dispatch(
          stateController.setState((prev) => ({
            ...prev,
            tasks: tasks.map((task) => (task.id === temporaryId ? { ...task, ...additionalTask } : task)),
          })),
        );

        dispatch(ProductionWorkflowActions.silentLoad({ id: productionWorkflowId, disableAdditionalTasksSet: false }));
      } catch (error) {
        notify.error(error.message);
      }
    };
  }

  public static updateAdditionalTask(id: string, updates: Partial<ProductionWorkflowTaskT>) {
    return async (dispatch, getState: () => AppState) => {
      const { tasks } = getState().production_workflow_additional_tasks;

      if (updates.status !== TaskStatusEnum.Reopened) {
        dispatch(
          stateController.setState((prev) => ({
            ...prev,
            tasks: tasks.map((task) => (task.id === id ? { ...task, ...updates } : task)),
          })),
        );
      }

      if (updates.status === TaskStatusEnum.Reopened) {
        const reasonOptions = await ProductionTaskService.getAllFailReasons();

        dispatch(ConfirmationWindowActions.setReasonOptions(reasonOptions));

        const targetTask = tasks.find((workflow) => workflow.id === id);

        if (targetTask) {
          const taskToReopen: TaskReopenModel = [
            {
              id: targetTask.id,
              name: targetTask.name,
              taskKey: targetTask.task_key,
            },
          ];

          dispatch(
            ModalActions.openModal<DeleteConfirmationOwnProps>({
              id: MODALS.CONFIRM,
              props: {
                title: <>Apply changes?</>,
                text: (
                  <ConfirmationWindow
                    description="Indicate the reason why task(s) didn’t pass the quality control. It will be saved and appear at production in task(s) that
                            didn’t pass the quality control."
                    tasksToReopen={taskToReopen}
                    onReasonChange={(values) => dispatch(ConfirmationWindowActions.onReasonChange(values))}
                    onReasonParametersChange={(values) => dispatch(ConfirmationWindowActions.onReasonParametersChange(values.id))}
                  />
                ),
                icon: <FireMinusIcon />,
                withCloseButton: false,
                actionText: 'Apply',
                countItems: 1,
                action: () => dispatch(Actions.handleApplyUpdateAdditionalTask(id, updates)),
                cancelAction: () => dispatch(ConfirmationWindowActions.disposeState()),
                onClickAway: () => dispatch(ConfirmationWindowActions.disposeState()),
              },
            }),
          );
        }
        return;
      }

      dispatch(Actions.handleApplyUpdateAdditionalTask(id, updates));
    };
  }

  static handleApplyUpdateAdditionalTask(id: string, value: Partial<ProductionWorkflowTaskT>) {
    return async (dispatch, getState: () => AppState) => {
      const { reasonForReopening, selectedReasonOptions } = getState().confirmation_window;

      const { page, productionId } = getState().manage_failed_tasks;

      const productionWorkflowId = getState().production_workflow.productionInfo.id;

      const { tasks } = getState().production_workflow_additional_tasks;

      const currentAdditionalTask = tasks.find((workflow) => workflow.id === id);

      try {
        const reasonIds = selectedReasonOptions.map((reason) => reason.id);

        const body: UpdateTaskBody = {
          make_assignment: false,
          ...value,
        };

        if (reasonIds && reasonIds.length > 0) {
          body.fail_reason_ids = reasonIds;
        }

        if (reasonForReopening?.trim()) {
          body.comment = reasonForReopening;
        }

        await ProductionTaskService.updateTask(id, body);

        if (page === 'production') {
          await dispatch(ProductionWorkflowActions.silentLoad({ id: productionWorkflowId, disableAdditionalTasksSet: false }));
        }

        if (page === 'task') {
          await dispatch(ManageFailedTasksActions.initAdditionalTasksData(productionId, false, false));
        }

        if (page === 'production_list') {
          await dispatch(ManageFailedTasksActions.initAdditionalTasksData(productionId, false, false));
        }

        dispatch(ConfirmationWindowActions.disposeState());
      } catch (error) {
        dispatch(
          stateController.setState((prev) => ({
            ...prev,
            tasks: tasks.map((task) => (task.id === id ? { ...task, ...currentAdditionalTask } : task)),
          })),
        );
        notify.error(error.message);
      }
    };
  }

  public static updateAdditionalTaskLockedIndicator(updatedTasks: ProductionWorkflowTaskTWithIssues[]) {
    return async (dispatch) => {
      dispatch(
        stateController.setState((prev) => ({
          ...prev,
          tasks: updatedTasks,
        })),
      );
    };
  }

  public static deleteTemporaryAdditionalTask(temporaryId: string) {
    return async (dispatch, getState: () => AppState) => {
      const { tasks } = getState().production_workflow_additional_tasks;

      const filteredAdditionalTasks = tasks.filter((item) => item.id !== temporaryId);

      dispatch(
        stateController.setState((prev) => ({
          ...prev,
          tasks: filteredAdditionalTasks,
        })),
      );
    };
  }

  public static deleteAdditionalTaskFromList(taskId: string) {
    return async (dispatch, getState: () => AppState) => {
      const { tasks } = getState().production_workflow_additional_tasks;
      const productionWorkflowId = getState().production_workflow.productionInfo.id;
      const { page, productionId } = getState().manage_failed_tasks;

      try {
        await ProductionTaskService.deleteTask(taskId);

        dispatch(Actions.onSetTasks(tasks.filter((item) => item.id !== taskId)));

        notify.success('Deleted successfully');

        if (page === 'task') {
          await dispatch(ManageFailedTasksActions.initAdditionalTasksData(productionId, false, false));
          return;
        }

        if (page === 'production_list') {
          await dispatch(ManageFailedTasksActions.initAdditionalTasksData(productionId, false, false));
          return;
        }

        if (page === 'production') {
          dispatch(ProductionWorkflowActions.silentLoad({ id: productionWorkflowId, disableAdditionalTasksSet: false }));
          dispatch(
            ProductionFiltersActions.getProductionsByFilter({
              showFetchEffect: false,
              paginationMode: PaginationMode.CurrentPage,
              resetSelectedProductions: false,
            }),
          );
        }
      } catch (error) {
        dispatch(
          stateController.setState((prevState) => ({
            ...prevState,
            tasks,
          })),
        );
        notify.error(error.message);
      }
    };
  }

  public static onSetTasks(workflowTaskTemplates: ProductionWorkflowAdditionalTaskT[]) {
    return async (dispatch, getState: () => AppState) => {
      const { tasks } = getState().production_workflow_additional_tasks;

      let updatedWorkflowTaskTemplates = [...workflowTaskTemplates];
      const outOfOrder = updatedWorkflowTaskTemplates.some((item, index) => item.order !== index);

      if (outOfOrder) {
        updatedWorkflowTaskTemplates = updatedWorkflowTaskTemplates.map((item, index) => ({
          ...item,
          order: index,
        }));
      }

      dispatch(
        stateController.setState((prev) => ({
          ...prev,
          tasks: updatedWorkflowTaskTemplates,
        })),
      );

      const manageOrder = updatedWorkflowTaskTemplates.map(({ id, order }) => ({ id, order }));

      try {
        await ProductionTaskService.manageTaskOrder({ tasks: manageOrder });
      } catch (err) {
        dispatch(
          stateController.setState((prev) => ({
            ...prev,
            tasks,
          })),
        );
      }
    };
  }

  public static openDeleteConfirmationModal(taskId) {
    return (dispatch) => {
      dispatch(
        ModalActions.openModal<DeleteConfirmationOwnProps>({
          id: MODALS.CONFIRM,
          props: {
            title: <>{t('additional_tasks.delete_additional_task_modal.title')}</>,
            text: (
              <Trans i18nKey="additional_tasks.delete_additional_task_modal.text">
                <div style={{ marginBottom: '7px' }}>Are you sure you want to delete the task?</div>,
              </Trans>
            ),
            icon: <FireMinusIcon />,
            withCloseButton: false,
            actionText: <>{t('global.button_delete')}</>,
            action: () => dispatch(Actions.deleteAdditionalTaskFromList(taskId)),
          },
        }),
      );
    };
  }

  public static copyAdditionalTaskLink(id: string) {
    return async () => {
      const currentDomain = window.location.hostname;

      const link = `${protocol}${currentDomain}/task/${id}`;

      await navigator.clipboard.writeText(link);
      notify.success('The link copied');
    };
  }

  public static handleUser(taskId: string, user: UserShortModel, slotId: string, type: 'assign' | 'unassign') {
    return async (dispatch, getState: () => AppState) => {
      const { tasks } = getState().production_workflow_additional_tasks;
      const isAssign = type === 'assign';
      const { page, productionId } = getState().manage_failed_tasks;

      try {
        dispatch(
          stateController.setState((prev) => ({
            ...prev,
            tasks: prev.tasks.map((task) => {
              return task.id === taskId
                ? {
                    ...task,
                    taskResponsibilities: task.taskResponsibilities.map((responsibility) => ({
                      ...responsibility,
                      taskSlots: responsibility.taskSlots.map((slot) => {
                        return slot.id === slotId
                          ? {
                              ...slot,
                              task_assignment_id: isAssign ? user.id : null,
                              taskAssignment: isAssign ? { user } : null,
                            }
                          : slot;
                      }),
                    })),
                  }
                : task;
            }),
          })),
        );
        if (isAssign) {
          await TaskAssigmentService.assignUser(user.id, slotId);
        } else {
          await TaskAssigmentService.unassignUser(slotId);
        }

        if (page === 'task') {
          await dispatch(ManageFailedTasksActions.initAdditionalTasksData(productionId, false, false));
        }
      } catch (error) {
        dispatch(stateController.setState((prev) => ({ ...prev, tasks })));
        notify.error('Something went wrong');
      }
    };
  }

  public static handleFailAdditionalTask(
    taskId: string,
    values: Partial<ProductionWorkflowTaskTWithIssues>,
    rootItemId?: string | null,
  ) {
    return (dispatch, getState: () => AppState) => {
      const { tasks } = getState().production_workflow_additional_tasks;

      dispatch(
        stateController.setState((prev) => ({
          ...prev,
          tasks: tasks.map((task) => {
            if (rootItemId) {
              if (task.id === rootItemId && task.reopenedTask?.id === taskId) {
                return {
                  ...task,
                  reopenedTask: {
                    ...task.reopenedTask,
                    ...values,
                  },
                };
              }
              return task;
            }
            return task.id === taskId ? { ...task, ...values } : task;
          }),
        })),
      );
    };
  }
}

export class Selectors {
  static countAdditionalTasks(state: AppState): number {
    return state.production_workflow_additional_tasks.tasks
      .filter((task) => !task.id.includes('new') && !task.failed_task_id)
      .reduce((total, task) => {
        const reopenedCount = task.reopenedTasks?.filter((el) => !el.id.includes('new')).length || 0;
        return total + 1 + reopenedCount;
      }, 0);
  }

  static getAdditionalTasksProgress(state: AppState): number {
    const { tasks } = state.production_workflow_additional_tasks;

    const allTasks = tasks
      .filter((task) => !task.id.includes('new') && !task.failed_task_id)
      .flatMap((task) => {
        const reopened = task.reopenedTasks?.filter((el) => !el.id.includes('new')) || [];
        return [task, ...reopened];
      });

    if (allTasks.length === 0) return 0;

    const tasksInDone = allTasks.filter((task) => taskDoneStatuses.includes(task.status));
    return Math.round((tasksInDone.length / allTasks.length) * 100);
  }
}

export const reducer = stateController.getReducer();
